import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/apiClient";
import { PageHeader, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowsClockwise, DownloadSimple } from "@phosphor-icons/react";

const startOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const today = () => new Date().toISOString().slice(0, 10);

function KPI({ label, value, tone = "slate", testid }) {
  const toneCls = {
    slate: "bg-white border-slate-200",
    orange: "bg-[#EA580C] text-white border-[#EA580C]",
    blue: "bg-[#2E75B6] text-white border-[#2E75B6]",
    dark: "bg-[#0F172A] text-white border-[#0F172A]",
  }[tone];
  return (
    <div className={`border ${toneCls} rounded-sm p-4`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-[0.14em] font-semibold ${tone === "slate" ? "text-slate-500" : "text-white/70"}`}>{label}</div>
      <div className="font-display text-[22px] font-bold font-mono-num mt-1">{value}</div>
    </div>
  );
}

function downloadCSV(name, rows) {
  if (!rows || !rows.length) return;
  const cols = Object.keys(rows[0]);
  const csv = [cols.join(",")]
    .concat(rows.map((r) => cols.map((c) => {
      const v = r[c] == null ? "" : String(r[c]).replace(/"/g, '""');
      return /[,"\n]/.test(v) ? `"${v}"` : v;
    }).join(",")))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export default function GSTReports() {
  const [from, setFrom] = useState(startOfMonth());
  const [to, setTo] = useState(today());
  const [g1, setG1] = useState(null);
  const [g3, setG3] = useState(null);
  const [summary, setSummary] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());

  const load = useCallback(async () => {
    const params = { from_date: from, to_date: to };
    const [a, b, c] = await Promise.all([
      api.get("/reports/gstr-1", { params }),
      api.get("/reports/gstr-3b", { params }),
      api.get("/reports/gst/summary", { params: { year } }),
    ]);
    setG1(a.data);
    setG3(b.data);
    setSummary(c.data);
  }, [from, to, year]);
  useEffect(() => { load(); }, [load]);

  const monthTiles = useMemo(() => {
    if (!summary) return [];
    const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return summary.months.map((m) => ({ ...m, label: names[m.month - 1] }));
  }, [summary]);

  return (
    <div data-testid="gst-reports-page">
      <PageHeader
        title="GST Reports — GSTR-1 & GSTR-3B"
        module="Finance · Taxation"
        subtitle="Auto-computed from Invoices & Purchases. Ready to reconcile with the GSTN portal or export for your CA."
        ribbon={
          <>
            <BcRibbonGroup label="Process">
              <BcRibbonBtn icon={ArrowsClockwise} label="Refresh" onClick={load} testid="gst-refresh" />
            </BcRibbonGroup>
          </>
        }
      />

      <div className="p-6 space-y-5">
        {/* Period picker */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="label-caps mb-1 block">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-44" data-testid="gst-from" />
          </div>
          <div>
            <Label className="label-caps mb-1 block">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-44" data-testid="gst-to" />
          </div>
          <div>
            <Label className="label-caps mb-1 block">Year (Summary)</Label>
            <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value) || year)} className="w-28 font-mono-num" data-testid="gst-year" />
          </div>
        </div>

        {/* Headline KPIs (from GSTR-3B) */}
        {g3 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="Output Tax" tone="slate" value={fmtMoney((g3.output_tax.igst + g3.output_tax.cgst + g3.output_tax.sgst) || 0)} testid="gst-kpi-output" />
            <KPI label="Input Tax Credit" tone="blue" value={fmtMoney((g3.input_tax_credit.igst + g3.input_tax_credit.cgst + g3.input_tax_credit.sgst) || 0)} testid="gst-kpi-itc" />
            <KPI label="Net Tax Payable" tone="orange" value={fmtMoney(g3.tax_payable.total || 0)} testid="gst-kpi-payable" />
            <KPI label="Invoices in Period" tone="dark" value={g1 ? g1.totals.invoice_count : 0} testid="gst-kpi-count" />
          </div>
        )}

        <Tabs defaultValue="gstr1" data-testid="gst-tabs">
          <TabsList>
            <TabsTrigger value="gstr1" data-testid="tab-gstr1">GSTR-1 · Outward</TabsTrigger>
            <TabsTrigger value="gstr3b" data-testid="tab-gstr3b">GSTR-3B · Summary</TabsTrigger>
            <TabsTrigger value="hsn" data-testid="tab-hsn">HSN Summary</TabsTrigger>
            <TabsTrigger value="monthly" data-testid="tab-monthly">12-Month Trend</TabsTrigger>
          </TabsList>

          {/* ---------- GSTR-1 ---------- */}
          <TabsContent value="gstr1" className="mt-5 space-y-6" data-testid="gstr1-panel">
            {g1 && (
              <>
                <BucketTable
                  title={`B2B (registered clients) — ${g1.b2b.length} rows`}
                  rows={g1.b2b}
                  onExport={() => downloadCSV(`gstr1-b2b-${from}-to-${to}.csv`, g1.b2b)}
                  cols={[
                    { key: "invoice_number", label: "Invoice #" },
                    { key: "date", label: "Date" },
                    { key: "client_name", label: "Client" },
                    { key: "client_gstin", label: "GSTIN", mono: true },
                    { key: "place_of_supply", label: "POS" },
                    { key: "taxable_value", label: "Taxable", num: true },
                    { key: "cgst", label: "CGST", num: true },
                    { key: "sgst", label: "SGST", num: true },
                    { key: "igst", label: "IGST", num: true },
                    { key: "invoice_value", label: "Total", num: true, bold: true },
                  ]}
                />
                <BucketTable
                  title={`B2C-Large (Inter-state · > ₹2.5L) — ${g1.b2cl.length} rows`}
                  rows={g1.b2cl}
                  onExport={() => downloadCSV(`gstr1-b2cl-${from}-to-${to}.csv`, g1.b2cl)}
                  cols={[
                    { key: "invoice_number", label: "Invoice #" },
                    { key: "date", label: "Date" },
                    { key: "client_name", label: "Client" },
                    { key: "place_of_supply", label: "POS" },
                    { key: "taxable_value", label: "Taxable", num: true },
                    { key: "igst", label: "IGST", num: true },
                    { key: "invoice_value", label: "Total", num: true, bold: true },
                  ]}
                />
                <BucketTable
                  title={`B2C-Small (aggregated by state × rate) — ${g1.b2cs.length} rows`}
                  rows={g1.b2cs}
                  onExport={() => downloadCSV(`gstr1-b2cs-${from}-to-${to}.csv`, g1.b2cs)}
                  cols={[
                    { key: "place_of_supply", label: "State (POS)" },
                    { key: "rate", label: "Rate %", num: true },
                    { key: "invoice_count", label: "# Invoices", num: true },
                    { key: "taxable_value", label: "Taxable", num: true },
                    { key: "cgst", label: "CGST", num: true },
                    { key: "sgst", label: "SGST", num: true },
                    { key: "igst", label: "IGST", num: true },
                    { key: "invoice_value", label: "Total", num: true, bold: true },
                  ]}
                />
              </>
            )}
          </TabsContent>

          {/* ---------- GSTR-3B ---------- */}
          <TabsContent value="gstr3b" className="mt-5" data-testid="gstr3b-panel">
            {g3 && (
              <div className="space-y-6">
                <Panel title="Table 3.1 — Details of Outward Supplies & Inward Supplies liable to Reverse Charge">
                  <Table className="bc-list">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nature of Supplies</TableHead>
                        <TableHead className="text-right">Taxable Value</TableHead>
                        <TableHead className="text-right">IGST</TableHead>
                        <TableHead className="text-right">CGST</TableHead>
                        <TableHead className="text-right">SGST</TableHead>
                        <TableHead className="text-right">Cess</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[
                        ["(a) Outward taxable supplies (other than zero, nil, exempt)", g3.table_3_1.a_taxable_outward],
                        ["(b) Outward taxable supplies (zero rated)", g3.table_3_1.b_zero_rated],
                        ["(c) Other outward supplies (Nil-rated, exempted)", g3.table_3_1.c_nil_rated_exempt],
                        ["(d) Inward supplies (liable to reverse charge)", g3.table_3_1.d_inward_reverse_charge],
                        ["(e) Non-GST outward supplies", g3.table_3_1.e_non_gst],
                      ].map(([lbl, row], i) => (
                        <TableRow key={`gstr3b-31-${lbl.slice(0, 8)}`} data-testid={`gstr3b-3_1-row-${i}`}>
                          <TableCell>{lbl}</TableCell>
                          <TableCell className="text-right font-mono-num">{fmtMoney(row.taxable_value)}</TableCell>
                          <TableCell className="text-right font-mono-num">{fmtMoney(row.igst)}</TableCell>
                          <TableCell className="text-right font-mono-num">{fmtMoney(row.cgst)}</TableCell>
                          <TableCell className="text-right font-mono-num">{fmtMoney(row.sgst)}</TableCell>
                          <TableCell className="text-right font-mono-num">{fmtMoney(row.cess)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Panel>

                <Panel title="Table 4 — Eligible ITC">
                  <Table className="bc-list">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Details</TableHead>
                        <TableHead className="text-right">IGST</TableHead>
                        <TableHead className="text-right">CGST</TableHead>
                        <TableHead className="text-right">SGST</TableHead>
                        <TableHead className="text-right">Cess</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[
                        ["(A) ITC Available", g3.table_4_itc.a_itc_available],
                        ["(B) ITC Reversed", g3.table_4_itc.b_reversed],
                        ["(C) Net ITC Available (A - B)", g3.table_4_itc.c_net_itc],
                      ].map(([lbl, row], i) => (
                        <TableRow key={`gstr3b-4-${lbl.slice(0, 3)}`} data-testid={`gstr3b-4-row-${i}`}>
                          <TableCell className={i === 2 ? "font-semibold" : ""}>{lbl}</TableCell>
                          <TableCell className={`text-right font-mono-num ${i === 2 ? "font-semibold" : ""}`}>{fmtMoney(row.igst)}</TableCell>
                          <TableCell className={`text-right font-mono-num ${i === 2 ? "font-semibold" : ""}`}>{fmtMoney(row.cgst)}</TableCell>
                          <TableCell className={`text-right font-mono-num ${i === 2 ? "font-semibold" : ""}`}>{fmtMoney(row.sgst)}</TableCell>
                          <TableCell className="text-right font-mono-num">{fmtMoney(row.cess)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Panel>

                <Panel title="Net Tax Payable">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KPI label="IGST payable" value={fmtMoney(g3.tax_payable.igst)} testid="gst-payable-igst" />
                    <KPI label="CGST payable" value={fmtMoney(g3.tax_payable.cgst)} testid="gst-payable-cgst" />
                    <KPI label="SGST payable" value={fmtMoney(g3.tax_payable.sgst)} testid="gst-payable-sgst" />
                    <KPI label="Total Payable" tone="orange" value={fmtMoney(g3.tax_payable.total)} testid="gst-payable-total" />
                  </div>
                </Panel>
              </div>
            )}
          </TabsContent>

          {/* ---------- HSN Summary ---------- */}
          <TabsContent value="hsn" className="mt-5" data-testid="hsn-panel">
            {g1 && (
              <BucketTable
                title={`HSN-wise summary — ${g1.hsn.length} rows`}
                rows={g1.hsn}
                onExport={() => downloadCSV(`gstr1-hsn-${from}-to-${to}.csv`, g1.hsn)}
                cols={[
                  { key: "hsn", label: "HSN", mono: true },
                  { key: "description", label: "Description" },
                  { key: "unit", label: "UQC" },
                  { key: "total_qty", label: "Total Qty", num: true },
                  { key: "rate", label: "Rate %", num: true },
                  { key: "taxable_value", label: "Taxable", num: true },
                  { key: "cgst", label: "CGST", num: true },
                  { key: "sgst", label: "SGST", num: true },
                  { key: "igst", label: "IGST", num: true },
                ]}
              />
            )}
          </TabsContent>

          {/* ---------- Monthly trend ---------- */}
          <TabsContent value="monthly" className="mt-5 space-y-4" data-testid="monthly-panel">
            {summary && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {monthTiles.map((m) => (
                    <div key={m.month} className="border border-slate-200 rounded-sm p-3 bg-white" data-testid={`month-tile-${m.month}`}>
                      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{m.label} {year}</div>
                      <div className="mt-1 text-[12.5px] flex justify-between font-mono-num">
                        <span className="text-slate-500">Out</span><span>{fmtMoney(m.output_tax)}</span>
                      </div>
                      <div className="text-[12.5px] flex justify-between font-mono-num">
                        <span className="text-slate-500">ITC</span><span>{fmtMoney(m.itc)}</span>
                      </div>
                      <div className="text-[12.5px] flex justify-between font-mono-num font-semibold text-[#EA580C] pt-1 border-t border-slate-100 mt-1">
                        <span>Net</span><span>{fmtMoney(m.net_payable)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <KPI label={`FY ${year} · Output Tax`} tone="slate" value={fmtMoney(summary.totals.output_tax)} testid="fy-output" />
                  <KPI label={`FY ${year} · ITC availed`} tone="blue" value={fmtMoney(summary.totals.itc)} testid="fy-itc" />
                  <KPI label={`FY ${year} · Net Payable`} tone="orange" value={fmtMoney(summary.totals.net_payable)} testid="fy-net" />
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bc-card p-4 bg-white border border-slate-200 rounded-sm">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-3">{title}</div>
      {children}
    </div>
  );
}

function BucketTable({ title, rows, cols, onExport }) {
  return (
    <div className="bc-card border border-slate-200 rounded-sm overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">{title}</div>
        {rows && rows.length > 0 && (
          <button onClick={onExport} className="text-[11px] font-semibold text-[#EA580C] hover:underline flex items-center gap-1" data-testid={`export-${title.split(" ")[0].toLowerCase()}`}>
            <DownloadSimple size={12} /> Export CSV
          </button>
        )}
      </div>
      {(!rows || !rows.length) ? (
        <div className="p-6 text-center text-sm text-slate-400">No rows in period.</div>
      ) : (
        <Table className="bc-list">
          <TableHeader>
            <TableRow>
              {cols.map((c) => (
                <TableHead key={c.key} className={c.num ? "text-right" : ""}>{c.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.invoice_number || r.hsn || `bucket-${i}`}>
                {cols.map((c) => {
                  const v = r[c.key];
                  const cls = [
                    c.num ? "text-right font-mono-num" : "",
                    c.mono ? "font-mono text-[11.5px]" : "",
                    c.bold ? "font-semibold" : "",
                  ].filter(Boolean).join(" ");
                  return (
                    <TableCell key={c.key} className={cls}>
                      {c.num && typeof v === "number" ? fmtMoney(v) : (v ?? "—")}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
